import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { MemoryHistory } from 'history';

import SettingsTile from 'components/common/tile/SettingsTile';
import { useAuthN } from 'hooks/auth/subscriptions';
import { Providers } from 'app-types';
import { useParamountPlusDisplayName } from 'hooks/kitchenSink';
import { VideoCtvRedux } from 'store-types';

const messages = {
  ttsRowTitle: {
    id: 'tile.settings.tts.rowTitle',
    description: 'tts for row title',
  },
  signInAdobe: {
    id: 'tile.settings.auth.adobe',
    description: 'Adobe tv provider',
  },
  signInHeader: {
    id: 'tile.settings.auth.signInHeader',
    description: 'sign in header',
  },
  signOutHeader: {
    id: 'tile.settings.auth.signOutHeader',
    description: 'sign out header',
  },
  ttsAuthAdobe: {
    id: 'tile.settings.tts.auth.adobe',
    description: 'tts for Adobe sign in provider',
  },
  ttsAuthAdobeSignOut: {
    id: 'tile.settings.tts.auth.signOutAdobe',
    description: 'tts for Adobe sign out provider',
  },
  ttsAuthParamountPlus: {
    id: 'tile.settings.tts.auth.paramountPlus',
    description: 'tts for P+ sign in provider',
  },
  ttsAuthParamountPlusSignOut: {
    id: 'tile.settings.tts.auth.signOutParamountPlus',
    description: 'tts for P+ sign out provider',
  },
};

type ProviderType = typeof Providers.ADOBE | typeof Providers.PARAMOUNT_PLUS;

interface AuthTileProps {
  id: string;
  provider: ProviderType;
  className?: string | null;
}

const AuthTile = ({
  id,
  provider,
  className = null,
}: AuthTileProps) => {
  const [renderFallback, setRenderFallback] = useState(false);
  const intl = useIntl();
  const providers =
    useSelector((state: VideoCtvRedux) => state.kitchenSink?.data?.providers) || {};
  const location = useLocation();
  const history = useHistory() as MemoryHistory;
  const hasParamountPlus = useAuthN([Providers.PARAMOUNT_PLUS]);
  const hasAdobe = useAuthN([Providers.ADOBE]);

  const paramountPlusLogo = providers?.paramountplus?.logoMediumUrl || '';
  const paramountPlusDisplayName = useParamountPlusDisplayName();

  const onClick = () => {
    const isProviderAuthenticated =
      (provider === Providers.PARAMOUNT_PLUS && hasParamountPlus) ||
      (provider === Providers.ADOBE && hasAdobe);
    history.push(
      {
        pathname: isProviderAuthenticated ? '/logout' : '/shortcode',
        search: `?providers=${provider}`,
      },
      {
        redirectTo: location,
      },
    );
  };

  if (provider === Providers.ADOBE) {
    return (
      <SettingsTile
        id={id}
        className={className}
        tts={intl.formatMessage(
          hasAdobe ? messages.ttsAuthAdobeSignOut : messages.ttsAuthAdobe,
        )}
        onClick={onClick}
      >
        <>
          <div data-testid="adobe-auth-header">
            <span>
              {intl.formatMessage(
                hasAdobe ? messages.signOutHeader : messages.signInHeader,
              )}
            </span>
          </div>
          <span data-testid="auth-message" className="text-uppercase">
            {intl.formatMessage(messages.signInAdobe)}
          </span>
        </>
      </SettingsTile>
    );
  }

  if (provider === Providers.PARAMOUNT_PLUS) {
    return (
      <SettingsTile
        id={id}
        className={className}
        tts={intl.formatMessage(
          hasParamountPlus
            ? messages.ttsAuthParamountPlusSignOut
            : messages.ttsAuthParamountPlus,
          { name: paramountPlusDisplayName },
        )}
        onClick={onClick}
      >
        <>
          <div data-testid="pp-auth-header">
            <span>
              {intl.formatMessage(
                hasParamountPlus
                  ? messages.signOutHeader
                  : messages.signInHeader,
              )}
            </span>
          </div>
          {renderFallback ? (
            <span className="text-uppercase">{paramountPlusDisplayName}</span>
          ) : (
            <img
              style={{ width: '170px' }}
              data-testid="auth-logo"
              src={paramountPlusLogo}
              onError={() => setRenderFallback(true)}
              alt={paramountPlusDisplayName}
            />
          )}
        </>
      </SettingsTile>
    );
  }

  return <div />;
};

export default React.memo(AuthTile);
